import { createSelector } from '@reduxjs/toolkit'

import { queryView, type QueryResultLike } from '@krp/client-runtime'

import type { NativeRead } from '../../pricing/api/envelope'
import type { ParseArgs } from '../api/importApi'
import { formatDateTime, formatMoney, formatQuantity } from '../../pricing/model/format'
import {
  commandRevision,
  firstConfigurationVersion,
  isUnknownOutcome,
  mutationState,
  type MutationLike,
} from '../../pricing/model/viewKit'
import { BYTES_PER_KIB, BYTES_PER_MIB, PREVIEW_LIMIT } from './constants'
import { attemptKey, emptyProfileDraft, mappingKey } from './importSlice'
import { MATCHED_BY_LABELS, PROFILE_ROLE_OPTIONS, ROLE_LABELS, ROLE_OPTIONS, SEVERITY_LABELS } from './labels'
import {
  DEFAULT_PROFILE_FILTER,
  EMPTY_MAPPING_DRAFT,
  EMPTY_UPLOAD_PARAMS,
  type FileVm,
  type ImportIntentState,
  type ImportProfile,
  type ImportedFile,
  type InvoiceColumnRole,
  type MappingEditorVm,
  type ParsedInvoice,
  type PreviewRowVm,
  type ProfileDraft,
  type ProfileEditorVm,
  type ProfilesVm,
  type ReportVm,
  type UploadFormVm,
  type ValidationReport,
  type WizardVm,
} from './types'

/**
 * Именованные селекторы импорта накладных (D-061). Вход — результаты endpoint-ов
 * и намерение фичи; RootState пакет не видит.
 */

// --- профили ----------------------------------------------------------------------------------------------

export interface ProfilesInput {
  resourceId: string
  intent: ImportIntentState
  query: QueryResultLike<NativeRead<readonly ImportProfile[]>>
  create: MutationLike
  update: MutationLike
  remove: MutationLike
  /**
   * Ревизия настроек базы из Platform (`configurationVersion` моего ресурса,
   * AU1b): с ней первая команда пишет сразу, не дожидаясь native-чтения. Ответ
   * самой службы свежее — он главнее; эта — только пока его нет.
   */
  resourceVersion?: string | null
}

/** Проверка формы профиля по правилам сервера (ImportProfileService): так человек видит причину до отправки. */
export function profileProblem(draft: ProfileDraft): string | null {
  if (draft.name.trim() === '') return 'Укажите имя профиля'
  if (draft.headerRowIndex.trim() !== '' && !/^\d+$/.test(draft.headerRowIndex.trim())) {
    return 'Номер строки шапки — целое от 0 (0 — шапки нет)'
  }
  if (draft.columns.length === 0) return 'Добавьте хотя бы одну колонку'
  const roles = new Set<InvoiceColumnRole>()
  for (const column of draft.columns) {
    const label = ROLE_LABELS[column.role]
    if (column.role === 'None') return 'Колонка без роли в профиле смысла не имеет'
    if (column.headerText.trim() === '' && column.columnIndex.trim() === '') {
      return `Колонка «${label}» не адресуема: укажите заголовок или номер`
    }
    if (column.columnIndex.trim() !== '' && !/^[1-9]\d*$/.test(column.columnIndex.trim())) {
      return `Колонка «${label}»: номер считается с 1`
    }
    if (roles.has(column.role)) return `Роль «${label}» стоит у двух колонок`
    roles.add(column.role)
  }
  return null
}

function profileSummary(profile: ImportProfile): string {
  return profile.columns.map((column) => ROLE_LABELS[column.role]).join(', ')
}

export const makeSelectProfilesVm = () => {
  const selectQuery = (input: ProfilesInput) => input.query
  const selectIntent = (input: ProfilesInput) => input.intent
  const selectRid = (input: ProfilesInput) => input.resourceId
  const selectCreate = (input: ProfilesInput) => input.create
  const selectUpdate = (input: ProfilesInput) => input.update
  const selectRemove = (input: ProfilesInput) => input.remove
  const selectVersion = createSelector(
    [selectQuery, (input: ProfilesInput) => input.resourceVersion],
    (query, resourceVersion) => commandRevision(resourceVersion, query),
  )

  const selectRows = createSelector([selectQuery], (query) =>
    (query.data === undefined || query.isError ? [] : query.data.data).map((profile) => ({
      id: profile.id,
      name: profile.name,
      supplier: profile.supplierName ?? '—',
      isActive: profile.isActive,
      columnsText: profileSummary(profile),
      usageText: profile.usageCount === 0 ? 'не применялся' : `применён ${String(profile.usageCount)} раз`,
      updatedText: formatDateTime(profile.updatedAt),
    })),
  )

  const selectEditor = createSelector(
    [selectIntent, selectRid, selectCreate, selectUpdate],
    (intent, resourceId, create, update): ProfileEditorVm | null => {
      const draft = intent.profileEditors[resourceId]
      if (draft === null || draft === undefined) return null
      const mutation = draft.id === null ? create : update
      const state = mutationState(mutation)
      const problem = profileProblem(draft)
      return {
        draft,
        problem,
        canSave: problem === null && !state.busy,
        busy: state.busy,
        error: state.error,
        unknownOutcome: isUnknownOutcome(mutation),
        roleOptions: PROFILE_ROLE_OPTIONS,
      }
    },
  )

  return createSelector(
    [selectRid, selectIntent, selectQuery, selectRows, selectEditor, selectRemove, selectVersion],
    (resourceId, intent, query, rows, editor, remove, configurationVersion): ProfilesVm => {
      const removal = mutationState(remove)
      return {
        resourceId,
        configurationVersion,
        query: queryView(query, (read) => read.data.length === 0),
        filter: intent.profileFilters[resourceId] ?? DEFAULT_PROFILE_FILTER,
        rows,
        editor,
        removal: { busy: removal.busy, error: removal.error },
      }
    },
  )
}

// --- отчёт ------------------------------------------------------------------------------------------------

function reportParts(report: ValidationReport) {
  const summary = [
    `Строк в накладной: ${String(report.rowsTotal)}`,
    `без замечаний: ${String(report.rowsValid)}`,
    `с ошибками: ${String(report.rowsWithErrors)}`,
    `с предупреждениями: ${String(report.rowsWithWarnings)}`,
  ]
  const declared = report.declaredTotal === null ? null : `заявлено ${formatMoney(report.declaredTotal)}`
  return {
    summary,
    totalsText: [`посчитано ${formatMoney(report.computedTotal)}`, declared].filter((part) => part !== null).join(', '),
    issues: report.issues.map((issue, index) => ({
      key: `${String(index)}:${issue.code}:${String(issue.rowNumber ?? '')}`,
      severity: issue.severity,
      severityLabel: SEVERITY_LABELS[issue.severity],
      where: [
        issue.rowNumber === null ? null : `строка ${String(issue.rowNumber)}`,
        issue.columnIndex === null ? null : `колонка ${String(issue.columnIndex)}`,
        issue.role === null ? null : ROLE_LABELS[issue.role],
      ]
        .filter((part) => part !== null)
        .join(', '),
      message: issue.message,
      code: issue.code,
    })),
  }
}

/** Отчёт проверки из ответа сервера: блокировка и замечания дословно. */
export function reportVmOf(query: QueryResultLike<NativeRead<ValidationReport>>): ReportVm {
  const view = queryView(query, () => false)
  if (view.status !== 'ready' || query.data === undefined) {
    return { query: view, isBlocked: false, summary: [], totalsText: null, validatedAtText: null, truncated: false, issues: [] }
  }
  return reportFromValue(query.data.data, view)
}

export function reportFromValue(report: ValidationReport, view: ReportVm['query'] = { status: 'ready', refreshing: false, error: null }): ReportVm {
  const parts = reportParts(report)
  return {
    query: view,
    isBlocked: report.isBlocked,
    summary: parts.summary,
    totalsText: parts.totalsText,
    validatedAtText: formatDateTime(report.validatedAt),
    truncated: report.isTruncated,
    issues: parts.issues,
  }
}

// --- мастер загрузки и разметки ---------------------------------------------------------------------------

/** Результат загрузки/разметки: мутация с аргументами и временем завершения. */
export interface ParseMutation extends MutationLike<NativeRead<ParsedInvoice>> {
  originalArgs?: ParseArgs
  fulfilledTimeStamp?: number
}

export interface WizardInput {
  resourceId: string
  intent: ImportIntentState
  profiles: QueryResultLike<NativeRead<readonly ImportProfile[]>>
  /** Последняя загрузка файла в этом окне. */
  upload: ParseMutation
  /** Последнее применение разметки в этом окне. */
  mapping: ParseMutation
  /**
   * Ревизия настроек базы из Platform (`configurationVersion` моего ресурса,
   * AU1b): с ней первая команда пишет сразу, не дожидаясь native-чтения. Ответ
   * самой службы свежее — он главнее; эта — только пока его нет.
   */
  resourceVersion?: string | null
}

function previewRows(parsed: ParsedInvoice): readonly PreviewRowVm[] {
  return parsed.rows.slice(0, PREVIEW_LIMIT).map((row) => ({
    rowNumber: row.sourceRowNumber,
    name: row.name ?? '—',
    sku: row.sku ?? '—',
    barcode: row.barcode ?? '—',
    quantityText: formatQuantity(row.quantity),
    priceText: formatMoney(row.purchasePrice),
    sumText: formatMoney(row.sum),
    notes: row.notes.join('; '),
  }))
}

/**
 * Разобранная накладная окна: самый свежий УСПЕШНЫЙ ответ загрузки или разметки.
 * Неудачная разметка не стирает показанное: человек правит и повторяет.
 */
export function latestParsed(upload: ParseMutation, mapping: ParseMutation): ParsedInvoice | null {
  const candidates = [upload, mapping]
    .filter((mutation) => mutation.isSuccess && mutation.data !== undefined)
    .sort((a, b) => (b.fulfilledTimeStamp ?? 0) - (a.fulfilledTimeStamp ?? 0))
  return candidates[0]?.data?.data ?? null
}

/** Разметка колонок: предложение сервера, поверх него — выбор человека. Дубли ролей видны до отправки. */
export function mappingEditorOf(
  parsed: ParsedInvoice,
  intent: ImportIntentState,
  resourceId: string,
  mutation: ParseMutation,
): MappingEditorVm {
  const draft = intent.mappings[mappingKey(resourceId, parsed.fileId)] ?? EMPTY_MAPPING_DRAFT
  const columns = parsed.mapping.columns.map((column) => {
    // Предложение ниже порога (isApplied=false) сервер не применил: без выбора человека роль не назначена.
    const role = draft.roles[String(column.columnIndex)] ?? (column.isApplied ? column.suggestedRole : 'None')
    return {
      columnIndex: column.columnIndex,
      columnText: `Колонка ${String(column.columnIndex)}`,
      headerText: column.headerText ?? '',
      role,
      suggested: column.suggestedRole,
      suggestionText: column.isApplied || column.suggestedRole === 'None' ? null : `похоже: ${ROLE_LABELS[column.suggestedRole]}`,
    }
  })
  const used = new Set<InvoiceColumnRole>()
  let duplicate: InvoiceColumnRole | null = null
  for (const column of columns) {
    if (column.role === 'None') continue
    if (used.has(column.role)) duplicate = column.role
    used.add(column.role)
  }
  const missingRequired = parsed.mapping.missingRequiredRoles
    .filter((role) => !used.has(role))
    .map((role) => ROLE_LABELS[role])
  const headerRowText = draft.headerRow ?? (parsed.headerRowIndex === null ? '' : String(parsed.headerRowIndex))
  let problem: string | null = null
  if (duplicate !== null) problem = `Роль «${ROLE_LABELS[duplicate]}» назначена двум колонкам`
  else if (headerRowText.trim() !== '' && !/^\d+$/.test(headerRowText.trim())) problem = 'Номер строки шапки — целое от 0'
  else if (draft.saveAsProfile && draft.profileName.trim() === '') problem = 'Укажите имя профиля или снимите «Сохранить как профиль»'
  // Состояние мутации относится к этому файлу, только если она была про него.
  const own = mutation.originalArgs?.kind === 'mapping' && mutation.originalArgs.fileId === parsed.fileId ? mutation : null
  const state = mutationState(own ?? { isUninitialized: true, isLoading: false, isError: false, isSuccess: false })
  return {
    columns,
    roleOptions: ROLE_OPTIONS,
    draft,
    sheetName: draft.sheetName ?? parsed.sheetName,
    headerRow: headerRowText,
    worksheets: parsed.worksheets.filter((sheet) => sheet.isVisible).map((sheet) => sheet.name),
    missingRequired,
    problem,
    canApply: problem === null && !state.busy,
    busy: state.busy,
    error: state.error,
    unknownOutcome: own !== null && isUnknownOutcome(own),
  }
}

export const makeSelectWizardVm = () => {
  const selectProfiles = (input: WizardInput) => input.profiles
  const selectUploadMutation = (input: WizardInput) => input.upload
  const selectMappingMutation = (input: WizardInput) => input.mapping
  const selectIntent = (input: WizardInput) => input.intent
  const selectRid = (input: WizardInput) => input.resourceId

  const selectUpload = createSelector(
    [selectIntent, selectRid, selectProfiles, selectUploadMutation],
    (intent, resourceId, profiles, upload): UploadFormVm => {
      const state = mutationState(upload)
      return {
        params: intent.uploadParams[resourceId] ?? EMPTY_UPLOAD_PARAMS,
        busy: state.busy,
        error: state.error,
        unknownOutcome: isUnknownOutcome(upload),
        profileOptions: [
          { value: '', label: 'Определить автоматически' },
          ...(profiles.data === undefined || profiles.isError
            ? []
            : profiles.data.data
                .filter((profile) => profile.isActive)
                .map((profile) => ({ value: profile.id, label: profile.name }))),
        ],
      }
    },
  )

  const selectParsedValue = createSelector([selectUploadMutation, selectMappingMutation], latestParsed)
  const selectVersion = createSelector(
    [selectProfiles, selectUploadMutation, selectMappingMutation, (input: WizardInput) => input.resourceVersion],
    (profiles, upload, mapping, resourceVersion) => {
      const fromParse = [upload, mapping].find((mutation) => mutation.isSuccess && mutation.data !== undefined)
      return firstConfigurationVersion(profiles) ?? fromParse?.data?.configurationVersion ?? commandRevision(resourceVersion, profiles)
    },
  )

  return createSelector(
    [selectRid, selectIntent, selectUpload, selectParsedValue, selectMappingMutation, selectVersion],
    (resourceId, intent, upload, parsed, mapping, configurationVersion): WizardVm => ({
      resourceId,
      configurationVersion,
      upload,
      parsed:
        parsed === null
          ? null
          : {
              fileId: parsed.fileId,
              fileName: parsed.fileName,
              matchedByText: MATCHED_BY_LABELS[parsed.mapping.matchedBy],
              profileText: parsed.mapping.profileName,
              notes: parsed.notes,
              preview: previewRows(parsed),
              previewTruncated: parsed.rows.length > PREVIEW_LIMIT,
              editor: mappingEditorOf(parsed, intent, resourceId, mapping),
              report: parsed.validation === null ? null : reportFromValue(parsed.validation),
            },
    }),
  )
}

// --- файл -------------------------------------------------------------------------------------------------

export interface FileInput {
  resourceId: string
  fileId: string
  file: QueryResultLike<NativeRead<ImportedFile>>
  report: QueryResultLike<NativeRead<ValidationReport>>
  validate: MutationLike
  /**
   * Ревизия настроек базы из Platform (`configurationVersion` моего ресурса,
   * AU1b): с ней первая команда пишет сразу, не дожидаясь native-чтения. Ответ
   * самой службы свежее — он главнее; эта — только пока его нет.
   */
  resourceVersion?: string | null
}

const NO_DOWNLOAD_ERROR: FileVm['download'] = { errorText: null }

function formatBytes(size: number): string {
  if (size < BYTES_PER_KIB) return `${String(size)} Б`
  if (size < BYTES_PER_MIB) return `${formatQuantity(size / BYTES_PER_KIB)} КиБ`
  return `${formatQuantity(size / BYTES_PER_MIB)} МиБ`
}

export const makeSelectFileVm = () => {
  const selectFile = (input: FileInput) => input.file
  const selectReport = (input: FileInput) => input.report
  const selectValidate = (input: FileInput) => input.validate
  const selectRid = (input: FileInput) => input.resourceId
  const selectFileId = (input: FileInput) => input.fileId

  const selectFileVm = createSelector([selectFile], (query): FileVm['file'] => {
    const view = queryView(query, () => false)
    const file = view.status === 'ready' && query.data !== undefined ? query.data.data : null
    return {
      query: view,
      freshness: null,
      info:
        file === null
          ? []
          : [
              { label: 'Файл', value: file.fileName },
              { label: 'Размер', value: formatBytes(file.sizeBytes) },
              { label: 'Загружен', value: `${formatDateTime(file.uploadedAt)}, ${file.uploadedBy}` },
              { label: 'Лист', value: file.sheetName ?? '—' },
              { label: 'Контрольная сумма SHA-256', value: file.sha256 },
              { label: 'Версия разбора', value: file.parserVersion },
            ],
      mapping:
        file?.effectiveMapping?.columns.map((column) => ({
          column: `Колонка ${String(column.columnIndex)}`,
          header: column.headerText ?? '',
          role: ROLE_LABELS[column.role],
        })) ?? [],
      matchedByText: file?.effectiveMapping == null ? null : MATCHED_BY_LABELS[file.effectiveMapping.matchedBy],
    }
  })
  const selectReportVm = createSelector([selectReport], (query) => reportVmOf(query))
  const selectVersion = createSelector(
    [selectFile, selectReport, (input: FileInput) => input.resourceVersion],
    (file, report, resourceVersion) => commandRevision(resourceVersion, file, report),
  )

  return createSelector(
    [selectRid, selectFileId, selectFileVm, selectReportVm, selectValidate, selectVersion],
    (resourceId, fileId, file, report, validate, configurationVersion): FileVm => {
      const state = mutationState(validate)
      return {
        resourceId,
        fileId,
        configurationVersion,
        file,
        report,
        validation: { busy: state.busy, error: state.error, unknownOutcome: isUnknownOutcome(validate) },
        download: NO_DOWNLOAD_ERROR,
      }
    },
  )
}

export { attemptKey, emptyProfileDraft }
